import React, { FC, memo } from 'react';
import { useMutation } from 'react-query';
import { Doctor } from 'models/doctor';
import {
  DeleteTwoTone,
  ExclamationCircleOutlined,
} from '@ant-design/icons/lib';
import { Button, message, Modal } from 'antd';
import { red } from '@ant-design/colors';
import { UsersAPI } from 'api/UsersAPI';
import { ButtonProps } from 'antd/lib/button';
import { FormattedMessage } from 'react-intl';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface DeleteDoctorButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  doctor: Doctor;
  onDeleted?: () => void;
}

export const DeleteDoctorButton: FC<DeleteDoctorButtonProps> = memo(
  ({ doctor, onDeleted, ...buttonProps }) => {
    const [mutate, { isLoading }] = useMutation(UsersAPI.deleteDoctor);
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title: intlMessage(
          { id: 'entity.delete.confirmation' },
          {
            name: `${doctor.firstName} ${doctor.lastName}`,
          }
        ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            { userId: doctor.userId },
            {
              onSuccess: () => {
                message.success(
                  intlMessage(
                    { id: 'entity.delete.success' },
                    {
                      name: `${doctor.firstName} ${doctor.lastName}`,
                    }
                  )
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    return (
      <Button
        id="button-delete-doctor"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<DeleteTwoTone twoToneColor={red.primary} />}
        danger
        {...buttonProps}
      >
        <FormattedMessage id="delete" tagName="span" />
      </Button>
    );
  }
);
